// src/components/DeviceSharingList.jsx
// 同じ端末からの出席（誰の端末で、誰の分が打刻されたか）を並べる【2026-11-02】
//   データは GET /api/attendance/device-sharing/:classId（services/deviceSharingService.js）。
//   出席受付のリアルタイム出席（LiveAttendancePanel）と、出席集計の「同じ端末」タブ（WsDeviceSharing）で使う。
//
//   端末の持ち主 = その端末でいちばん多く打刻している人。
//   持ち主が決まれば「持ち主が、ほかの人の分を打った」と書く。決まらなければ並べるだけ。

import React from 'react';

const STATUS = { present: '出席', late: '遅刻', early_leave: '早退', absent: '欠席', irregular: '要確認', excused: '公欠' };

export const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (d) => {
  if (!d) return '';
  const x = new Date(String(d).slice(0, 10) + 'T00:00:00');
  if (isNaN(x.getTime())) return String(d).slice(0, 10);
  return `${x.getMonth() + 1}/${x.getDate()}（${'日月火水木金土'[x.getDay()]}）`;
};
const who = (e) => `${e.name || '（名前なし）'}（${e.studentId}）`;

/** 1つの回×端末を、1行の文で言う */
export function sentenceOf(g) {
  const others = g.entries.filter(e => !e.isOwner);
  const list = (arr) => arr.map(e => `${who(e)} ${fmtTime(e.time)}`).join('、');
  if (g.owner.decided && !g.owner.outsider) {
    const ownerName = `${g.owner.name}（${g.owner.studentId}）`;
    return g.ownerTapped
      ? { lead: `${ownerName}の端末から、ほかの人の分も打刻されています`, who: list(others) }
      : { lead: `${ownerName}の端末から、本人ではない人の分が打刻されています（${g.owner.name}さん本人の打刻はこの回にありません）`, who: list(others) };
  }
  if (g.owner.decided && g.owner.outsider) {
    return { lead: 'この授業の学生ではない人がふだん使っている端末から、打刻されています', who: list(g.entries) };
  }
  return { lead: '同じ端末から複数の人が打刻しています（どの人の端末かははっきりしません）', who: list(g.entries) };
}

/**
 * groups … API の groups
 * compact … リアルタイム出席用（日付を出さない・表を出さない）
 */
export default function DeviceSharingList({ groups, compact = false }) {
  if (!groups || groups.length === 0) return null;
  return (
    <div className="dsh-list">
      {groups.map(g => {
        const s = sentenceOf(g);
        return (
          <div key={g.key} className="dsh-item">
            <div className="dsh-head">
              {!compact && <span className="dsh-date">{fmtDate(g.date)}</span>}
              <span className="dsh-device" title="端末の記号（端末の識別子から作った短い記号。同じ記号なら同じ端末）">端末 {g.device}</span>
              <span className="dsh-lead">{s.lead}</span>
            </div>
            {compact ? (
              <div className="dsh-who">{s.who}</div>
            ) : (
              <table className="data-table dsh-table">
                <thead><tr><th>時刻</th><th>学籍番号</th><th>氏名</th><th>状態</th><th>この端末では</th></tr></thead>
                <tbody>
                  {g.entries.map(e => (
                    <tr key={e.attendanceId} className={e.isOwner ? 'dsh-owner-row' : (g.owner.decided ? 'dsh-proxied-row' : '')}>
                      <td>{fmtTime(e.time)}</td>
                      <td>{e.studentId}</td>
                      <td>{e.name}</td>
                      <td>{STATUS[e.status] || e.status}</td>
                      <td>{e.isOwner ? '持ち主（本人の打刻）' : (g.owner.decided ? '代わりに打刻された' : '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!compact && g.owner.decided && (
              <div className="dsh-basis">
                持ち主の決め方：この端末の打刻 {g.owner.totalTaps}回のうち {g.owner.tapsOnDevice}回が
                {g.owner.outsider ? 'この授業の学生ではない人' : `${g.owner.name}さん`}（すべての授業を合わせた数）
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * 学生ごとのまとめ：代わりに打刻された回数／ほかの人の分を打った回数（出席集計のタブ用）
 */
export function summarizeByStudent(groups) {
  const m = new Map();
  const get = (e) => {
    if (!m.has(e.userId)) m.set(e.userId, { userId: e.userId, studentId: e.studentId, name: e.name, proxied: 0, proxying: 0, unclear: 0 });
    return m.get(e.userId);
  };
  for (const g of groups || []) {
    if (g.owner.decided && !g.owner.outsider) {
      const ownerEntry = g.entries.find(e => e.isOwner);
      const others = g.entries.filter(e => !e.isOwner);
      if (ownerEntry) get(ownerEntry).proxying += others.length;
      others.forEach(e => { get(e).proxied++; });
    } else if (g.owner.decided && g.owner.outsider) {
      g.entries.forEach(e => { get(e).proxied++; });
    } else {
      g.entries.forEach(e => { get(e).unclear++; });
    }
  }
  return [...m.values()].sort((a, b) => (b.proxied + b.proxying + b.unclear) - (a.proxied + a.proxying + a.unclear) || String(a.studentId).localeCompare(String(b.studentId)));
}
